import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/auth/token";
import type { Widget, WidgetParameter } from "./kinds/types";

interface LookupOption {
  label: string;
  value: unknown;
}

/** Union of every widget's declared parameters, keyed by name.
 *  When two widgets declare the same param name we keep the first one's
 *  type + default — they should be filter-compatible already. */
function paramUnion(widgets: Widget[]): WidgetParameter[] {
  const seen = new Map<string, WidgetParameter>();
  for (const w of widgets) {
    for (const p of w.parameters ?? []) {
      if (!seen.has(p.name)) seen.set(p.name, p);
    }
  }
  return Array.from(seen.values());
}

interface FilterBarProps {
  widgets: Widget[];
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}

export function FilterBar({ widgets, values, onChange }: FilterBarProps) {
  const params = useMemo(() => paramUnion(widgets), [widgets]);
  const apiUrl = import.meta.env.VITE_API_URL ?? "";

  // Resolved lookup options per param name.
  const [lookups, setLookups] = useState<Record<string, LookupOption[]>>({});
  const [loadingLookups, setLoadingLookups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    const lookupParams = params.filter(
      (p) => p.lookup_sql && !(p.name in lookups),
    );
    if (lookupParams.length === 0) return;
    (async () => {
      for (const p of lookupParams) {
        setLoadingLookups((s) => ({ ...s, [p.name]: true }));
        try {
          const r = await apiFetch(`${apiUrl}/v1/widgets/lookup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sql: p.lookup_sql,
              label_col: p.lookup_label_col ?? "label",
              value_col: p.lookup_value_col ?? "value",
            }),
          });
          if (!r.ok || cancelled) continue;
          const body = (await r.json()) as { ok: boolean; options?: LookupOption[] };
          if (cancelled) continue;
          const opts = body.options ?? [];
          // Prepend "All" sentinel so users can clear.
          const withAll = [{ label: "All", value: null }, ...opts];
          setLookups((s) => ({ ...s, [p.name]: withAll }));
        } finally {
          if (!cancelled) {
            setLoadingLookups((s) => ({ ...s, [p.name]: false }));
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // We only depend on params identity — re-running on every values change
    // would burn lookups for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, apiUrl]);

  if (params.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        py: 1,
        px: 1.25,
        mb: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        alignItems: "center",
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ width: "100%" }}>
        {params.map((p) => {
          const current = values[p.name] ?? p.default ?? "";

          // Dropdown via lookup
          if (p.lookup_sql) {
            const opts = lookups[p.name];
            const loading = loadingLookups[p.name];
            if (loading || !opts) {
              return (
                <Box
                  key={p.name}
                  sx={{
                    minWidth: 180,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CircularProgress size={14} />
                </Box>
              );
            }
            if (p.multi) {
              // Multi-select: render chips inside a Select; value is array.
              const selected = Array.isArray(current)
                ? (current as unknown[])
                : current
                  ? [current]
                  : [];
              const selectedStrs = selected.map((s) => String(s));
              return (
                <TextField
                  key={p.name}
                  select
                  label={prettify(p.name)}
                  size="small"
                  value={selectedStrs}
                  slotProps={{
                    select: {
                      multiple: true,
                      renderValue: (val) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {(val as string[]).map((s) => {
                            const opt = opts.find((o) => String(o.value) === s);
                            return (
                              <Chip
                                key={s}
                                label={opt?.label ?? s}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      ),
                    },
                  }}
                  onChange={(e) => {
                    const raw = e.target.value as unknown as string[] | string;
                    const arr = Array.isArray(raw) ? raw : [raw];
                    const next = arr
                      .map((s) => opts.find((o) => String(o.value) === s)?.value)
                      .filter((v) => v !== undefined && v !== null);
                    onChange({ ...values, [p.name]: next });
                  }}
                  sx={{ minWidth: { xs: "100%", sm: 240 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                >
                  {opts
                    // Skip the "All" sentinel — multi-select expresses "all"
                    // as zero chips selected.
                    .filter((o) => o.value !== null)
                    .map((o, i) => {
                      const sv = String(o.value);
                      const checked = selectedStrs.indexOf(sv) > -1;
                      return (
                        <MenuItem key={i} value={sv}>
                          <Checkbox checked={checked} />
                          <ListItemText primary={o.label} />
                        </MenuItem>
                      );
                    })}
                </TextField>
              );
            }
            return (
              <TextField
                key={p.name}
                select
                label={prettify(p.name)}
                size="small"
                value={(current ?? "") as string}
                onChange={(e) => {
                  const raw = e.target.value;
                  const opt = opts.find((o) => String(o.value) === raw);
                  onChange({
                    ...values,
                    [p.name]: opt ? opt.value : raw,
                  });
                }}
                sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
              >
                {opts.map((o, i) => (
                  <MenuItem key={i} value={String(o.value ?? "")}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            );
          }

          if (p.type === "date" || p.type === "datetime") {
            return (
              <TextField
                key={p.name}
                label={prettify(p.name)}
                type="date"
                size="small"
                value={typeof current === "string" ? current.slice(0, 10) : ""}
                onChange={(e) => onChange({ ...values, [p.name]: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: { xs: "47%", sm: 150 }, flex: { xs: "1 1 47%", sm: "0 0 auto" } }}
              />
            );
          }
          if (p.type === "number" || p.type === "integer") {
            return (
              <TextField
                key={p.name}
                label={prettify(p.name)}
                type="number"
                size="small"
                value={current as number | string}
                onChange={(e) => onChange({ ...values, [p.name]: e.target.value })}
                sx={{ minWidth: { xs: "47%", sm: 140 }, flex: { xs: "1 1 47%", sm: "0 0 auto" } }}
              />
            );
          }
          return (
            <TextField
              key={p.name}
              label={prettify(p.name)}
              size="small"
              value={current as string}
              onChange={(e) => onChange({ ...values, [p.name]: e.target.value })}
              sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

function prettify(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
